import React, { useEffect, useState } from 'react';
import { Award, Star, Users, Camera, Sun, Compass, Hotel, Mountain, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import HeroSection from '../components/HeroSection';
import { useNavigate } from 'react-router-dom';

const ICON_MAP = {
  award: <Award size={32} className="text-kenya-orange" />,
  star: <Star size={32} className="text-kenya-orange" />,
  users: <Users size={32} className="text-kenya-orange" />,
  camera: <Camera size={32} className="text-kenya-orange" />,
  sun: <Sun size={32} className="text-kenya-orange" />,
  compass: <Compass size={32} className="text-kenya-orange" />,
  hotel: <Hotel size={28} className="text-kenya-orange" />,
  mountain: <Mountain size={28} className="text-kenya-orange" />,
};

const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.85 },
  show: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", duration: 0.8 } }
};

const HomePage = () => {
  const [features, setFeatures] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); 

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://python.rock-safaris.com/home/');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        console.log('API Response:', data); // Debug log
        
        setFeatures(data.features || []);
        setDestinations(data.destinations || []);
        setServices(data.services || []);
        setError(null);
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
        
        // Fallback data for testing
        setFeatures([
          {
            icon: 'award',
            title: 'Expert Guides',
            description: 'Professional guides with years of experience'
          },
          {
            icon: 'star',
            title: '5-Star Service',
            description: 'Premium service quality guaranteed'
          },
          {
            icon: 'users',
            title: 'Group Tours',
            description: 'Perfect for families and groups'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openWhatsApp = (message) => {
    const phoneNumber = '+254792400119';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Handler for destination "Learn More" - redirects to contact page
  const handleDestinationLearnMore = (destinationName) => {
    navigate('/contact', { 
      state: { 
        message: `I want to learn more about ${destinationName}.`,
        subject: `Inquiry about ${destinationName}`
      }
    });
  };

  // Handler for services "Learn More" - redirects to contact page
  const handleServiceLearnMore = (serviceName) => {
    navigate('/contact', { 
      state: { 
        message: `I want to learn more about your ${serviceName} service.`,
        subject: `Inquiry about ${serviceName}`
      }
    });
  };

  if (loading) {
    return (
      <div>
        <HeroSection />
        <section className="bg-kenya-white py-5">
          <div className="container">
            <h2 className="text-center fw-bold mb-5 text-kenya-brown">Why Choose Hunters Rock Tours?</h2>
            <div className="text-center">
              <div className="spinner-border text-kenya-orange" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <HeroSection />

      {/* Features Section */}
      <section className="bg-kenya-white py-5">
        <div className="container">
          <h2 className="text-center fw-bold mb-5 text-kenya-brown">Why Choose Hunters Rock Tours?</h2>
          
          {error && (
            <div className="alert alert-warning text-center" role="alert">
              <small>Using demo data due to connection issue: {error}</small>
            </div>
          )}
          
          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            {features.length > 0 ? (
              features.map((feature, idx) => (
                <motion.div
                  className="col-md-4 col-lg-4 col-sm-6"
                  key={idx}
                  variants={cardVariants}
                >
                  <div className="card border-0 shadow h-100 text-center">
                    <div className="card-body">
                      <div className="mb-3">
                        {ICON_MAP[feature.icon] || <Star size={32} className="text-kenya-orange" />}
                      </div>
                      <h5 className="card-title fw-semibold mb-2">{feature.title}</h5>
                      <p className="card-text">{feature.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-12">
                <div className="text-center text-muted">
                  <p>No features available at the moment.</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Destinations Section */}
      <section className="py-5 bg-kenya-yellow">
        <div className="container">
          <h2 className="text-center fw-bold mb-5 text-kenya-brown">Popular Destinations</h2>
          <div className="row g-4">
            {destinations.map((destination, idx) => (
              <div className="col-md-4 col-lg-4 col-sm-6" key={idx}>
                <div className="card h-100 shadow border-0">
                  <img
                    src={
                      destination.image?.startsWith('http')
                        ? destination.image
                        : `https://python.rock-safaris.com${destination.image}`
                    }
                    alt={destination.name}
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x200/f4f4f4/666?text=No+Image';
                    }}
                  />
                  <div className="card-body">
                    <h5 className="card-title fw-semibold">{destination.name}</h5>
                    <p className="card-text">{destination.description}</p>
                    <button
                      className="btn btn-link text-kenya-orange fw-semibold p-0"
                      onClick={() => handleDestinationLearnMore(destination.name)}
                    >
                      Learn More <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-5 bg-kenya-white">
        <div className="container">
          <h2 className="text-center fw-bold mb-5 text-kenya-brown">Our Services</h2>
          <div className="row g-4">
            {services.map((service, idx) => (
              <div className="col-md-3 col-sm-6" key={idx}>
                <div className="card border-0 shadow h-100 text-center">
                  <div className="card-body">
                    <div className="mb-2">
                      {ICON_MAP[service.icon] || <Compass size={28} className="text-kenya-orange" />}
                    </div>
                    <h6 className="fw-semibold mb-2">{service.title}</h6>
                    <p className="card-text small">{service.description}</p>
                    <button
                      className="btn btn-link text-kenya-orange fw-semibold p-0"
                      onClick={() => handleServiceLearnMore(service.title)}
                    >
                      Learn More <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-5 bg-kenya-orange text-white text-center">
        <div className="container py-4">
          <h2 className="fw-bold mb-3">Ready for Your Next Adventure?</h2>
          <p className="lead mb-4" style={{ color: 'var(--kenya-yellow)' }}>
            Let us help you plan an unforgettable Kenyan experience!
          </p>
          <button
            className="btn btn-light text-kenya-orange fw-semibold px-4 py-2 shadow"
            onClick={() => openWhatsApp('Hello, I would like to book a safari with you.')}
          >
            Book Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;